/**
 * Badge for a trend value with icon (e.g. "+9.3%" with up arrow).
 * Use size="xs" for compact contexts (e.g. SparkChartRow); default size otherwise.
 */
import {
  ArrowRightIcon,
  TrendDownIcon,
  TrendUpIcon,
} from "@phosphor-icons/react";
import type * as React from "react";

import { cn } from "@/lib/utils";

export type TrendBadgeVariant = "positive" | "negative" | "neutral";

export type TrendBadgeSize = "default" | "xs";

const variantStyles: Record<TrendBadgeVariant, string> = {
  positive:
    "bg-green-100 text-green-800 dark:bg-green-400/20 dark:text-green-500",
  negative: "bg-rose-100 text-rose-800 dark:bg-rose-400/20 dark:text-rose-500",
  neutral: "bg-gray-100 text-gray-800 dark:bg-gray-500/30 dark:text-gray-300",
};

const TrendIcons = {
  positive: TrendUpIcon,
  negative: TrendDownIcon,
  neutral: ArrowRightIcon,
};

const sizeStyles: Record<TrendBadgeSize, string> = {
  default: "gap-x-1 rounded-md px-2 py-1 text-xs font-normal",
  xs: "gap-x-1 rounded px-2 py-1 text-[10px] font-normal leading-none",
};

const iconSizeBySize: Record<TrendBadgeSize, string> = {
  default: "size-4",
  xs: "size-3",
};

export interface TrendBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant: TrendBadgeVariant;
  /** Trend text shown next to the icon (e.g. "+9.3%", "-1.9%"). */
  value: string | number;
  /** "xs" for compact use (e.g. SparkChartRow); "default" otherwise. */
  size?: TrendBadgeSize;
}

const TrendBadge = ({
  variant,
  value,
  size = "default",
  className,
  ...props
}: TrendBadgeProps) => {
  const Icon = TrendIcons[variant];
  const displayContent = String(value);

  return (
    <span
      className={cn(
        "inline-flex w-fit items-center shrink-0 whitespace-nowrap",
        sizeStyles[size],
        variantStyles[variant],
        className,
      )}
      {...props}
    >
      <Icon
        className={cn("-ml-0.5 shrink-0", iconSizeBySize[size])}
        aria-hidden
      />
      {displayContent}
    </span>
  );
};

export { TrendBadge };
